import { DEFAULT_DOWN_IMG, DEFAULT_NO_IMG, DEFAULT_UP_IMG, DEFAULT_YSE_IMG } from '../confs/app';
import { marked, MarkedOptions, Tokens } from 'marked';

// Escape a value for use inside a double-quoted HTML attribute. Without this an
// alt/title containing a space (common in machine-translated content) would
// terminate the attribute early and corrupt the tag.
const escapeAttr = (s: unknown) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const readerMarkdown = async (
  markdown: string,
  options?: MarkedOptions | null
): Promise<[Tokens.Image[], string]> => {
  const images: Tokens.Image[] = [];
  const imgRegex = /<img\s+[^>]*src\s*=\s*["']([^"']+)["'][^>]*>/gi;
  let match;
  while ((match = imgRegex.exec(markdown)) !== null) {
    const href = match[1];
    const imgToken = {
      type: 'image',
      raw: match[0],
      href: href,
      title: '',
      text: '',
    } as Tokens.Image;
    const altMatch = match[0].match(/alt\s*=\s*["']([^"']*)["']/i);
    if (altMatch) {
      imgToken.text = altMatch[1];
    }
    const titleMatch = match[0].match(/title\s*=\s*["']([^"']*)["']/i);
    if (titleMatch) {
      imgToken.title = titleMatch[1];
    }
    images.push(imgToken);
  }
  const renderer = {
    image(img: Tokens.Image) {
      images.push(img);
      return `<img src="${escapeAttr(img.href)}" title="${escapeAttr(img.title)}" alt="${escapeAttr(img.text)}" rel="noopener noreferrer"/>`;
    },
    link({ href, text }: Tokens.Link) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
    html({ text }: Tokens.HTML | Tokens.Tag) {
      return text.replace(/\s+style\s*=\s*["'][^"']*["']/gi, '').replace(/\s+class\s*=\s*["'][^"']*["']/gi, '');
    },
  };
  const markdownHtml = await marked.use({ renderer }).parse(markdown, options);
  const img = images.filter(img => img.href.startsWith('https://'));
  return [img, markdownHtml];
};

export const extractImages = async (markdown: string, count: number = 4): Promise<string[]> => {
  const [images] = await readerMarkdown(markdown);
  return images.slice(0, count).map(img => img.href);
};

export const getDefaultImg = (description: string) => {
  switch (description.toLowerCase()) {
    case 'yes':
      return DEFAULT_YSE_IMG;
    case 'no':
      return DEFAULT_NO_IMG;
    case 'up':
      return DEFAULT_UP_IMG;
    case 'down':
      return DEFAULT_DOWN_IMG;
    default:
      return undefined;
  }
};
